import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { ContactDetailDrawer } from '@/components/contacts/ContactDetailDrawer';

const ContactCardContext = createContext(null);

export function ContactCardProvider({ children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [detailId, setDetailId] = useState(null);
  const [tags, setTags] = useState([]);
  const tagsLoaded = useRef(false);

  // Live contact-name cache: id -> current name. Lets any UI that only kept a
  // name snapshot (quick-invite recipients, etc.) show the up-to-date name so
  // renames propagate everywhere without a page reload.
  const [names, setNames] = useState({});
  const pending = useRef(new Set());
  const flushTimer = useRef(null);

  const flushNames = useCallback(async () => {
    flushTimer.current = null;
    const ids = Array.from(pending.current);
    pending.current = new Set();
    if (!ids.length) return;
    try {
      const { data } = await api.post('/contacts/names', { ids });
      if (data?.names) setNames((prev) => ({ ...prev, ...data.names }));
    } catch { /* names simply fall back to the stored snapshot */ }
  }, []);

  const registerNames = useCallback((ids) => {
    const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
    let queued = false;
    list.forEach((id) => {
      if (names[id] === undefined && !pending.current.has(id)) {
        pending.current.add(id);
        queued = true;
      }
    });
    if (queued && !flushTimer.current) {
      flushTimer.current = setTimeout(flushNames, 60);
    }
  }, [names, flushNames]);

  const liveName = useCallback((id) => (id ? names[id] : undefined), [names]);

  // Apply a fresh contact name to the cache (e.g. right after an inline edit).
  const cacheName = useCallback((id, name) => {
    if (!id) return;
    setNames((prev) => ({ ...prev, [id]: name || '' }));
  }, []);

  const ensureTags = useCallback(async () => {
    if (tagsLoaded.current) return;
    tagsLoaded.current = true;
    try {
      const { data } = await api.get('/contacts');
      setTags(data.filter_tags || []);
    } catch { /* tag pool simply stays minimal */ }
  }, []);

  // ident: { contactId?, name?, email?, phone?, telegram? }
  const openContact = useCallback(async (ident) => {
    if (!ident) return;
    ensureTags();
    if (ident.contactId) { setDetailId(ident.contactId); return; }
    const hasIdentity = ident.name || ident.email || ident.phone || ident.telegram;
    if (!hasIdentity) return;
    try {
      const { data } = await api.post('/contacts/resolve', {
        name: ident.name || '', email: ident.email || '',
        phone: ident.phone || '', telegram: ident.telegram || '',
      });
      setDetailId(data.id);
    } catch (e) {
      if (e?.response?.status === 404) toast.error(t('contacts.not_in_contacts'));
      else toast.error(t('common.error'));
    }
  }, [ensureTags, t]);

  return (
    <ContactCardContext.Provider value={{ openContact, liveName, registerNames }}>
      {children}
      <ContactDetailDrawer
        contactId={detailId}
        suggestedTags={tags}
        allTags={[]}
        onClose={() => setDetailId(null)}
        onPatched={(updated) => { if (updated?.id) cacheName(updated.id, updated.name); }}
        onPlan={(c, extra) => {
          setDetailId(null);
          navigate('/quick-invites/new', {
            state: {
              initialData: {
                title: extra?.title || '',
                invitee: c.email || c.telegram || c.phone || '',
                contact_id: c.id,
                invitee_name: c.name || '',
              },
            },
          });
        }}
      />
    </ContactCardContext.Provider>
  );
}

export function useContactCard() {
  return useContext(ContactCardContext) || {
    openContact: () => {}, liveName: () => undefined, registerNames: () => {},
  };
}
